// src/app/[locale]/sign-up/SignUpView.tsx
"use client";
import React, { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter, useSearchParams, useParams } from "next/navigation";
import { useGoogleLogin } from "@react-oauth/google";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslations } from "next-intl";
import { monitoring } from "@/services/monitoring";
import { handleError } from "@/utils/errorHandler";
import { useUserActions } from "@/hooks/useUserActions";
import {
  LogIn,
  Mail,
  User,
  Lock,
  AlertCircle,
  Loader2,
  CheckCircle2,
  Eye,
  EyeOff,
} from "lucide-react";
import { exchangeToken, validateInvitationToken } from "./actions";
import { loginWithEmail, registerWithEmail } from "./emailRegistrationActions";
import { getRedirectPathForRole } from "@/utils/users/roles";
import { getRole } from "@/service/jwtService";

const SignUpView: React.FC = () => {
  const t = useTranslations("SignUp");
  const params = useParams();
  const locale = (params.locale as string) || "en";
  const [error, setError] = useState<{ message: string; code?: string } | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleSignInLoading, setIsGoogleSignInLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isValidatingInvitation, setIsValidatingInvitation] = useState(false);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const router = useRouter();
  const { isAuthenticated, user, loading, updateAuth } = useAuth();
  const searchParams = useSearchParams();
  const invitationToken = searchParams.get("invitationToken");
  const isJustVerified = searchParams.get("verified") === "true";
  const [activeTab, setActiveTab] = useState<"login" | "register">(invitationToken ? "register" : "login");
  const { getUserProfile } = useUserActions();

  // Login form state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // Register form state
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [registerName, setRegisterName] = useState("");

  useEffect(() => {
    const handleAuthenticatedUserRedirect = async () => {
      if (!loading && isAuthenticated() && user?.roles) {
        try {
          const userRole = getRole(user.roles);
          const redirectPath = await getRedirectPathForRole(userRole, user.sub, locale);
          setIsRedirecting(true);
          router.push(redirectPath);
        } catch (error) {
          handleError(error, { component: "SignUpView", action: "handleAuthenticatedUserRedirect" });
        }
      }
    };

    handleAuthenticatedUserRedirect();
  }, [loading, isAuthenticated, user, router, locale]);

  useEffect(() => {
    if (!invitationToken) return;

    const validate = async () => {
      setIsValidatingInvitation(true);
      const result = await validateInvitationToken(invitationToken, locale);
      if (!result.valid) {
        setInvitationError(result.error?.message || t("Errors.InvalidInvitation"));
      }
      setIsValidatingInvitation(false);
    };

    validate();
  }, [invitationToken, locale]);

  const signInWithGoogle = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      setIsGoogleSignInLoading(true);
      setError(null);
      setIsRedirecting(true);

      try {
        const result = await exchangeToken(
          codeResponse.code,
          {
            invitationToken: invitationToken || undefined,
          },
          locale
        );

        if (!result.success) {
          if (
            result.error?.code === "USER_HAS_ROLES" ||
            result.error?.code === "INVALID_INVITATION" ||
            result.error?.code === "ROLE_CONFLICT" ||
            result.error?.code === "STAFF_VENUE_CONFLICT" ||
            result.error?.code === "USER_ALREADY_EXISTS"
          ) {
            setError({
              message: result.error.message,
              code: result.error.code,
            });
            setIsRedirecting(false);
            return;
          }
          monitoring.captureMessage("Invalid credentials during sign-up", {
            component: "SignUpView",
            action: "googleSignIn",
            errorType: "invalidCredentials",
            errorCode: result.error?.code,
            errorMessage: result.error?.message
          });
          setError({ message: t("Errors.InvalidCredentials") });
          setIsRedirecting(false);
          return;
        }

        const userData = result.data?.user;

        // Navigate first, then update auth context — this way notification
        // loading fires on the settled destination page rather than mid-navigation,
        // avoiding a stuck spinner caused by an in-flight server action during
        // route transition.
        router.push(result.data?.redirectPath || "/");
        updateAuth(userData);
      } catch (error) {
        monitoring.captureException(error, {
          component: "SignUpView",
          action: "googleSignIn",
          errorType: typeof error,
          errorMessage: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined
        });
        setError({
          message:
            t("Errors.UnexpectedError") ||
            "An unexpected error occurred. Please try again.",
        });
        setIsRedirecting(false);
      } finally {
        setIsGoogleSignInLoading(false);
      }
    },
    flow: "auth-code",
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const result = await loginWithEmail(
        {
          email: loginEmail,
          password: loginPassword,
        },
        locale
      );

      if (!result.success) {
        if (result.error?.code === "UNVERIFIED_EMAIL") {
          const encryptedId = localStorage.getItem(
            `verifyId_${loginEmail.toLowerCase()}`
          );
          router.push(
            encryptedId
              ? `/verify-email?id=${encryptedId}`
              : `/verify-email`
          );
          return;
        }
        setError({
          message: result.error?.message || t("Errors.InvalidCredentials"),
          code: result.error?.code,
        });
        return;
      }

      if (result.data?.user) {
        const userData = result.data.user;

        // Navigate first, then update auth context — this way notification
        // loading fires on the settled destination page rather than mid-navigation,
        // avoiding a stuck spinner caused by an in-flight server action during
        // route transition.
        router.push(result.data.redirectPath || "/");
        updateAuth(userData);
      }
    } catch (err) {
      setError({
        message:
          error?.message ||
          t("Errors.UnexpectedError") ||
          "An unexpected error occurred. Please try again.",
        code: error?.code,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const result = await registerWithEmail(
        {
          firstName: registerName,
          email: registerEmail,
          password: registerPassword,
          invitationToken: invitationToken || undefined,
        },
        locale
      );

      if (!result.success) {
        setError({
          message: result.error?.message || t("Errors.UserAlreadyExists"),
          code: result.error?.code,
        });
        return;
      }

      if (result.data?.encryptedId) {
        localStorage.setItem(
          `verifyId_${result.data.email.toLowerCase()}`,
          result.data.encryptedId
        );
        router.push(`/verify-email?id=${result.data.encryptedId}`);
      }
    } catch (err) {
      setError({
        message:
          t("Errors.UnexpectedError") ||
          "An unexpected error occurred. Please try again.",
        code: "UNKNOWN_ERROR",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 relative">
            <div className="absolute inset-0 bg-blue-500/20 rounded-full animate-ping"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
            </div>
          </div>
          <p className="text-white font-medium">
            {t("Loading")}
          </p>
        </div>
      </div>
    );
  }

  // Show loading state while validating invitation token
  if (isValidatingInvitation) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 relative">
            <div className="absolute inset-0 bg-blue-500/20 rounded-full animate-ping"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
            </div>
          </div>
          <p className="text-white font-medium">
            {t("ValidatingInvitation")}
          </p>
        </div>
      </div>
    );
  }

  // Show error state if invitation token is invalid
  if (invitationError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-xl p-8 text-center"
          >
            <div className="inline-block bg-red-500/20 rounded-full p-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {t("InvalidInvitationTitle")}
            </h1>
          </motion.div>
        </div>
      </div>
    );
  }

  // Show loading state while redirecting authenticated users
  if (isRedirecting || (isAuthenticated() && user?.roles)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 relative">
            <div className="absolute inset-0 bg-blue-500/20 rounded-full animate-ping"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
            </div>
          </div>
          <p className="text-white font-medium">
            {t("RedirectingToAccount") || "Redirecting to your account..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4 pt-20">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-xl overflow-hidden"
        >
          {/* Header */}
          <div className="p-6 pb-0 text-center">
            <div className="inline-block bg-gradient-to-r from-blue-500 to-purple-500 rounded-full p-3 mb-4">
              <LogIn className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-1">
              {t("Title")}
            </h1>
            <p className="text-gray-400 mb-6">
              {t("Description")}
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            {isJustVerified && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-start gap-3"
              >
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-green-400 text-sm">{t("VerificationSuccessBanner")}</p>
              </motion.div>
            )}

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3"
              >
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-400 text-sm">{error.message}</p>
              </motion.div>
            )}

            {/* Google Sign In */}
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              onClick={() => signInWithGoogle()}
              disabled={isGoogleSignInLoading}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-gradient-to-r from-blue-500/10 to-blue-600/10 hover:from-blue-500/20 hover:to-blue-600/20 border border-blue-500/30 text-white rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group mb-6"
            >
              {isGoogleSignInLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
              ) : (
                <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center text-blue-500 font-bold text-sm">
                  G
                </div>
              )}
              <span className="font-medium">
                {isGoogleSignInLoading
                  ? t("SigningIn")
                  : t("ContinueWithGoogle")}
              </span>
            </motion.button>

            {/* Tabs for Login/Register */}
            <>
              <div className="flex items-center gap-4 mb-6">
                  <div className="h-px bg-gray-700/50 flex-grow"></div>
                  <span className="text-gray-400 text-sm">
                    {t("OrContinueWithEmail")}
                  </span>
                  <div className="h-px bg-gray-700/50 flex-grow"></div>
                </div>

                {!invitationToken && (
                  <div className="flex mb-6">
                    <button
                      onClick={() => setActiveTab("login")}
                      className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === "login"
                          ? "border-blue-500 text-blue-500"
                          : "border-gray-700 text-gray-400 hover:text-gray-300"
                      }`}
                    >
                      {t("LoginTab")}
                    </button>
                    <button
                      onClick={() => setActiveTab("register")}
                      className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === "register"
                          ? "border-blue-500 text-blue-500"
                          : "border-gray-700 text-gray-400 hover:text-gray-300"
                      }`}
                    >
                      {t("RegisterTab")}
                    </button>
                  </div>
                )}

                <AnimatePresence mode="wait">
                  {activeTab === "login" ? (
                    <motion.form
                      key="login-form"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.2 }}
                      onSubmit={handleLogin}
                      className="space-y-4"
                    >
                      <div className="relative">
                        <input
                          type="email"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white pl-11 placeholder-gray-400 focus:border-blue-500/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          placeholder={t("FormLabels.Email")}
                          required
                        />
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      </div>

                      <div className="relative">
                        <input
                          type={showLoginPassword ? "text" : "password"}
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white pl-11 pr-11 placeholder-gray-400 focus:border-blue-500/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          placeholder={t("FormLabels.Password")}
                          required
                        />
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <button
                          type="button"
                          onClick={() =>
                            setShowLoginPassword(!showLoginPassword)
                          }
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                        >
                          {showLoginPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>

                      <div className="flex justify-end">
                        <Link
                          href="/forgot-password"
                          className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          {t("FormLabels.ForgotPassword")}
                        </Link>
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 group"
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>{t("Buttons.LoggingIn")}</span>
                          </>
                        ) : (
                          <>
                            <span>{t("Buttons.Login")}</span>
                            <CheckCircle2 className="w-0 h-0 group-hover:w-5 group-hover:h-5 opacity-0 group-hover:opacity-100 transition-all duration-300" />
                          </>
                        )}
                      </button>
                    </motion.form>
                  ) : (
                    <motion.form
                      key="register-form"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      onSubmit={handleRegister}
                      className="space-y-4"
                    >
                      <div className="relative">
                        <input
                          type="text"
                          value={registerName}
                          onChange={(e) => setRegisterName(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white pl-11 placeholder-gray-400 focus:border-blue-500/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          placeholder={t("FormLabels.FullName")}
                          required
                        />
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      </div>

                      <div className="relative">
                        <input
                          type="email"
                          value={registerEmail}
                          onChange={(e) => setRegisterEmail(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white pl-11 placeholder-gray-400 focus:border-blue-500/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          placeholder={t("FormLabels.Email")}
                          required
                        />
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      </div>

                      <div className="relative">
                        <input
                          type={showRegisterPassword ? "text" : "password"}
                          value={registerPassword}
                          onChange={(e) => setRegisterPassword(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white pl-11 pr-11 placeholder-gray-400 focus:border-blue-500/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          placeholder={t("FormLabels.Password")}
                          required
                        />
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <button
                          type="button"
                          onClick={() =>
                            setShowRegisterPassword(!showRegisterPassword)
                          }
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                        >
                          {showRegisterPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>

                      <div className="text-xs text-gray-400">
                        {t("FormLabels.PasswordRequirements")}
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 group"
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>{t("Buttons.CreatingAccount")}</span>
                          </>
                        ) : (
                          <>
                            <span>{t("Buttons.CreateAccount")}</span>
                            <CheckCircle2 className="w-0 h-0 group-hover:w-5 group-hover:h-5 opacity-0 group-hover:opacity-100 transition-all duration-300" />
                          </>
                        )}
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>
            </>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SignUpView;
